import React, { useContext, useEffect, useState } from 'react';
import * as yup from 'yup';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { ColWithGap, RowWithSpaceBetween } from '@components/styles';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import ErrorCountBadge from '@components/ringil3/Badges/ErrorBadge';
import useForm from '@hooks/useForm/useForm';
import { VerticalFieldAndValueWithIcon } from '@components/ringil3/Containers/VerticalFieldAndValueWithIcon';
import { VerticalFieldAndValue } from '@components/ringil3/Containers/VerticalFieldAndValue';
import {
  ColOdd,
  HorizontalLine,
  InputAndButtonGrid,
  OrderWrapper,
  ThreeColGridWithoutGap,
} from '@components/ringil3/styles';
import { findValidationMessage, findValidationMessageRaw } from '@utils/validation';
import InputTextWithSuffix from '@components/ringil3/Input/InputTextWithSuffix/InputTextWithSuffix';
import { isNotNullOrUndefined } from '@utils/utils';
import { InputTextarea } from 'primereact/inputtextarea';
import InputSKU from '@components/ringil3/Input/InputSKU/InputSKU';
import InputNoteWithLink from '@components/ringil3/Input/InputNoteWithLink/InputNoteWithLink';
import { Order, orderSchema } from '@components/framework/orders/common';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { ReservationFlagDTO } from '@api/ringil-api.v2';
import { FlagIcon } from '@components/ringil3/Features/Reservation/flagIcon';
import {
  CROSS_DOCK,
  crossDockDiscrepancyErrorMessage,
  findCrossDockDiscrepancyError,
  totalOrderUnits,
} from '@components/RingilComponents/form/flagUnitsLimit';

interface ComponentProps {
  reservationId: string;
  applicationId: string;
  onRefresh: () => void;
}

interface Form {
  orders: Order[];
  /** Actual cross dock units; empty means "as planned" and clears a stored value on save. */
  crossDockDiscrepancyUnits?: number;
}

export const useCreateEditReservationOrderItemsDiscrepancySidebar = () => {
  const [visible, setVisible] = useState(false);

  const EditReservationOrderItemsDiscrepancySidebar: React.FC<ComponentProps> = ({ reservationId, applicationId, onRefresh }) => {
    const { tr } = useTranslationLgs();
    const { loggedUser } = useContext(AuthenticatedUserContext);
    const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
    const { getReservationDetail, updateOrderDiscrepancies } = useContext(ApiContext);
    // Only a cross dock with planned units gets an actual value; without the flag the backend rejects one.
    const [crossDockFlag, setCrossDockFlag] = useState<ReservationFlagDTO>();

    useEffect(() => {
      const ac = new AbortController();

      getReservationDetail(
        reservationId,
        d => {
          if (ac.signal.aborted) return;
          const crossDock = d.flags?.find(f => f.reservationFlagType === CROSS_DOCK && f.units != null);
          setCrossDockFlag(crossDock);
          form.overwriteValues({
            crossDockDiscrepancyUnits: crossDock?.discrepancyUnits ?? null,
            orders:
              d.orders.length > 0
                ? d.orders.map(
                    t =>
                      ({
                        id: t.id,
                        orderName: t.orderName,
                        orderAmount: t.orderAmount,
                        orderDescription: t.orderDescription,
                        orderDiscrepancyNote: t.discrepancyNote,
                        items: t.items.map(i => ({
                          id: i.id,
                          amount: i.amount,
                          cargoTemplateCode: i.cargoTemplate?.code,
                          stackable: i.stackable,
                          discrepancyAmount: i.discrepancyAmount ?? i.amount,
                        })),
                      } as Order),
                  )
                : [
                    {
                      items: [],
                      orderName: null,
                      orderAmount: null,
                      orderDescription: null,
                    },
                  ],
          });
        },
        null,
        { onValidationFailed: setBeValidationErrors },
      );
      return () => {
        ac.abort();
      };
    }, [reservationId]);

    const form = useForm<Form>(
      (values: Form) =>
        yup.object().shape({
          orders: orderSchema(),
          crossDockDiscrepancyUnits: yup
            .number()
            .nullable()
            .test('crossDockDiscrepancy', '', function(value) {
              if (crossDockFlag == null) return true;
              const error = findCrossDockDiscrepancyError(crossDockFlag.units, value ?? undefined, values?.orders);
              return error == null || this.createError({ message: crossDockDiscrepancyErrorMessage(tr, error, totalOrderUnits(values?.orders)) });
            }),
        }),
      null,
      d => save(d),
      false,
      false,
      null,
      'scroll',
    );

    const mapDiscrepancies = (data: Form) => {
      return {
        reservationId,
        // Full state: leaving it out clears the stored value, so it is always sent while the flag exists.
        crossDockDiscrepancyUnits: crossDockFlag != null ? data.crossDockDiscrepancyUnits ?? null : undefined,
        orderDiscrepancies: data.orders.map(order => ({
          id: order.id,
          discrepancyNote: order.orderDiscrepancyNote,
          discrepancies: order.items.map(item => ({
            id: item.id,
            discrepancyAmount: item.discrepancyAmount, // Adjust this field as needed
          })),
        })),
      };
    };

    const save = (data: Form) => {
      updateOrderDiscrepancies(
        mapDiscrepancies(data),
        () => {
          setVisible(false);
          onRefresh();
        },
        null,
        { onValidationFailed: setBeValidationErrors },
      );
    };

    return (
      <>
        <RingilSidebar
          visible={visible}
          onBack={() => setVisible(false)}
          heading={
            <RowWithSpaceBetween>
              <div>{tr('EditOrderItemsDiscrepancySidebar.title', 'Update order real state')}</div>
              <ErrorCountBadge errorCount={new Set(form.validationErrors.map(t => t.path)).size} />
            </RowWithSpaceBetween>
          }
          onContinue={() => {
            form.validateAndSend();
          }}
        >
          {isNotNullOrUndefined(form.values) && (
            <>
              <BackendValidationComponent />
              <ColWithGap>
                <VerticalFieldAndValueWithIcon
                  label={tr(`EditOrderItemsDiscrepancySidebar.forReservation`, `For reservation`)}
                  valueText={applicationId}
                  iconPath={'/images/icons/ringil3/forExpedition.svg'}
                />
                <HorizontalLine variant={'tertiary'} />
                <ColOdd>
                  {form.values.orders.map((order, orderIndex) => {
                    return (
                      <OrderWrapper key={orderIndex}>
                        <VerticalFieldAndValue
                          label={tr(`EditOrderItemsDiscrepancySidebar.order`, `Order`)}
                          labelMode={'normal'}
                          errorMessage={findValidationMessage(form.validationErrors, `orders[${orderIndex}].orderName`, tr)}
                        >
                          <InputAndButtonGrid>
                            <InputTextarea
                              value={order.orderName}
                              style={{ width: '100%' }}
                              onChange={e => form.setFieldValue(`orders[${orderIndex}].orderName`, e.target.value)}
                              disabled={true}
                              autoResize={true}
                            />
                          </InputAndButtonGrid>
                        </VerticalFieldAndValue>
                        {order.items.map((item, itemIndex) => {
                          return (
                            <React.Fragment key={itemIndex}>
                              <ThreeColGridWithoutGap>
                                <VerticalFieldAndValue
                                  label={tr(`EditOrderItemsDiscrepancySidebar.manipulationUnit`, `Manipulation unit SKU`)}
                                  labelMode={'normal'}
                                  errorMessage={findValidationMessage(form.validationErrors, `orders[${orderIndex}].items[${itemIndex}].cargoTemplateCode`, tr)}
                                >
                                  <InputSKU
                                    fullHeight={true}
                                    value={item.cargoTemplateCode}
                                    companyId={loggedUser.companyId}
                                    onChange={v => form.setFieldValue(`orders[${orderIndex}].items[${itemIndex}].cargoTemplateCode`, v)}
                                    disabled={true}
                                  />
                                </VerticalFieldAndValue>
                                <VerticalFieldAndValue
                                  label={tr(`EditOrderItemsDiscrepancySidebar.amount`, `Amount`)}
                                  labelMode={'normal'}
                                  errorMessage={findValidationMessage(form.validationErrors, `orders[${orderIndex}].items[${itemIndex}].amount`, tr)}
                                >
                                  <InputTextWithSuffix
                                    fullHeight={true}
                                    suffix={'ks'}
                                    inputTextPosition={'right'}
                                    value={item.amount}
                                    onChange={() => {}}
                                    disabled={true}
                                  />
                                </VerticalFieldAndValue>
                                <VerticalFieldAndValue
                                  label={tr(`EditOrderItemsDiscrepancySidebar.amountDiscrepancy`, `Discrepancy`)}
                                  labelMode={'normal'}
                                  errorMessage={findValidationMessage(form.validationErrors, `orders[${orderIndex}].items[${itemIndex}].discrepancyAmount`, tr)}
                                >
                                  <InputTextWithSuffix
                                    fullHeight={true}
                                    suffix={'ks'}
                                    inputTextPosition={'right'}
                                    value={item.discrepancyAmount}
                                    onChange={v => form.setFieldValue(`orders[${orderIndex}].items[${itemIndex}].discrepancyAmount`, v)}
                                  />
                                </VerticalFieldAndValue>
                              </ThreeColGridWithoutGap>
                            </React.Fragment>
                          );
                        })}
                        <InputNoteWithLink
                          note={order.orderDiscrepancyNote}
                          onChanges={v => form.setFieldValue(`orders[${orderIndex}].orderDiscrepancyNote`, v)}
                          noteLabel={tr(`EditOrderItemsDiscrepancySidebar.orderDiscrepancyNote`, `Order discrepancy note`)}
                        />
                      </OrderWrapper>
                    );
                  })}
                </ColOdd>
                {crossDockFlag != null && (
                  <OrderWrapper>
                    {/* items-start: an error under the actual units must not stretch the other cells and push the flag title down. */}
                    <ThreeColGridWithoutGap className="items-start">
                      <VerticalFieldAndValue label={tr(`EditOrderItemsDiscrepancySidebar.flag`, `Flag`)}
                                             labelMode={'normal'}>
                        <span className="inline-flex items-center gap-x-1 h-[2.625rem]">
                          <FlagIcon type={crossDockFlag.reservationFlagType} className="w-4 shrink-0" />
                          {tr(`ReservationFlag.${crossDockFlag.reservationFlagType}`, crossDockFlag.reservationFlagType)}
                        </span>
                      </VerticalFieldAndValue>
                      <VerticalFieldAndValue
                        label={tr(`EditOrderItemsDiscrepancySidebar.crossDockUnits`, `Planned units`)}
                        labelMode={'normal'}>
                        <InputTextWithSuffix fullHeight={true} suffix={'ks'} inputTextPosition={'right'}
                                             value={crossDockFlag.units} onChange={() => {
                        }} disabled={true} />
                      </VerticalFieldAndValue>
                      <VerticalFieldAndValue
                        label={tr(`EditOrderItemsDiscrepancySidebar.crossDockDiscrepancyUnits`, `Actual units`)}
                        labelMode={'normal'}
                        errorMessage={findValidationMessageRaw(form.validationErrors, 'crossDockDiscrepancyUnits')?.message}
                      >
                        <InputTextWithSuffix
                          fullHeight={true}
                          suffix={'ks'}
                          inputTextPosition={'right'}
                          type={'number'}
                          value={form.values.crossDockDiscrepancyUnits}
                          onChange={v => form.setFieldValue('crossDockDiscrepancyUnits', v === '' || isNaN(Number(v)) ? null : Number(v))}
                        />
                      </VerticalFieldAndValue>
                    </ThreeColGridWithoutGap>
                  </OrderWrapper>
                )}
              </ColWithGap>
            </>
          )}
        </RingilSidebar>
      </>
    );
  };

  return { EditReservationOrderItemsDiscrepancySidebar, setVisible, visible };
};
